<template>
  <div class="pa-4">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2>Deadline Categories</h2>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog"
        >Add Category</v-btn
      >
    </div>

    <v-progress-linear v-if="store.loading" indeterminate></v-progress-linear>
    <v-alert v-if="store.error" type="error" class="mb-4">{{
      store.error
    }}</v-alert>

    <v-card
      v-if="!store.loading && store.categories.length === 0"
      class="pa-8 text-center"
    >
      <v-icon size="64" color="grey-lighten-1" class="mb-4"
        >mdi-tag-outline</v-icon
      >
      <h3 class="text-h6 mb-2">No Deadline Categories</h3>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Create your first category.
      </p>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog"
        >Add Category</v-btn
      >
    </v-card>

    <v-card v-if="!store.loading && store.categories.length > 0">
      <v-list>
        <v-list-item
          v-for="category in store.categories"
          :key="category.id"
          class="border-b"
        >
          <template #prepend>
            <v-avatar
              size="28"
              :style="{ backgroundColor: category.color || '#eee' }"
            >
              <v-icon :color="category.color ? 'white' : 'primary'">{{
                category.icon || "mdi-tag"
              }}</v-icon>
            </v-avatar>
          </template>
          <v-list-item-title>{{ category.name }}</v-list-item-title>
          <v-list-item-subtitle v-if="category.icon || category.color">
            <span class="text-caption"
              >Icon: {{ category.icon || "mdi-tag" }} | Color:
              {{ category.color || "—" }}</span
            >
          </v-list-item-subtitle>
          <template #append>
            <div class="d-flex ga-2">
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click="openEditDialog(category)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click="confirmDelete(category)"
              ></v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>{{
          isEditing ? "Edit Category" : "Add Category"
        }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="formData.name"
              label="Name"
              :rules="nameRules"
              required
              autofocus
            ></v-text-field>
            <IconPickerField v-model="formData.icon" label="Icon (mdi-*)" />
            <ColorPickerField
              v-model="formData.color"
              label="Color"
              hint="#1976D2 or 'red'"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid"
            @click="save"
            >{{ isEditing ? "Update" : "Create" }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text
          >Are you sure you want to delete "{{ categoryToDelete?.name }}"?
          Deadlines will be reassigned to "Other".</v-card-text
        >
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteCategory"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useDeadlineCategoriesStoreRefactor } from "@/stores/deadlineCategoriesStoreRefactor";
import ColorPickerField from "@/components/ui/ColorPickerField.vue";
import IconPickerField from "@/components/ui/IconPickerField.vue";

const store = useDeadlineCategoriesStoreRefactor();

const dialog = ref(false);
const deleteDialog = ref(false);
const formValid = ref(false);
const saving = ref(false);
const deleting = ref(false);
const isEditing = ref(false);
const categoryToDelete = ref(null);

const formData = reactive({ id: null, name: "", icon: "", color: "" });

const nameRules = [
  (v) => !!v || "Name is required",
  (v) => (v && v.length <= 255) || "Name must be less than 255 characters",
];

const openCreateDialog = () => {
  isEditing.value = false;
  formData.id = null;
  formData.name = "";
  formData.icon = "";
  formData.color = "";
  dialog.value = true;
};

const openEditDialog = (category) => {
  isEditing.value = true;
  formData.id = category.id;
  formData.name = category.name;
  formData.icon = category.icon || "";
  formData.color = category.color || "";
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
};

const save = async () => {
  if (!formValid.value) return;
  saving.value = true;
  try {
    const payload = {
      name: formData.name,
      icon: formData.icon || null,
      color: formData.color || null,
    };
    if (isEditing.value) {
      await store.update({ id: formData.id, ...payload });
    } else {
      await store.create(payload);
    }
    dialog.value = false;
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (category) => {
  categoryToDelete.value = category;
  deleteDialog.value = true;
};

const deleteCategory = async () => {
  if (!categoryToDelete.value) return;
  deleting.value = true;
  try {
    await store.destroy(categoryToDelete.value.id);
    deleteDialog.value = false;
    categoryToDelete.value = null;
  } finally {
    deleting.value = false;
  }
};

onMounted(async () => {
  await store.fetchAll();
});
</script>
